import axios from 'axios';

const api = axios.create({
  baseURL: '/api'
});

api.interceptors.request.use((config) => {
  // Tenant portal calls pass their own Authorization header explicitly (the
  // tenantToken, via getTenantHeaders()) — never clobber it with the admin
  // token. A browser that's logged into both the admin panel and the tenant
  // portal at once (same person, same machine) has BOTH 'token' and
  // 'tenantToken' in localStorage; overwriting unconditionally here silently
  // sent the admin's token on tenant requests, which requireTenantAuth then
  // verified into a payload with no `phone` claim at all (403 "אין גישה").
  if (!config.headers.Authorization) {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    // 403 here means requireTenantAuth found the stored tenantToken's phone
    // doesn't match the tenantPhone being requested (a stale token left over
    // from a previous phone/session in this browser — e.g. the logout button
    // used to clear tenantPhone but not tenantToken). Treat it the same as an
    // expired session: clear tenant state and force a clean re-login, instead
    // of leaving the tenant stuck on a permanent "אין גישה" error.
    const isTenantAuthMismatch = error.response?.status === 403 &&
      (window.location.pathname.startsWith('/portal') || window.location.pathname.startsWith('/tenant-projects'));
    if (error.response?.status === 401 || isTenantAuthMismatch) {
      // This axios instance is shared by both the tenant portal and the admin
      // panel — a 401 on a tenant API call (e.g. an expired tenantToken after
      // idle logout) must NOT wipe admin state and send the tenant to the admin
      // login. Route each session back to its own login based on where it is.
      const isTenant = isTenantAuthMismatch || window.location.pathname.startsWith('/portal') || window.location.pathname.startsWith('/tenant-projects');
      if (isTenant) {
        ['tenantPhone','tenantToken','tenantName','tenantProject','tenantProjects','tenantBuildingAddress','tenantBuildingFull','tenantApartmentNumber','tenantBAddr','tenantBNum','tenantAptNum','tenantIsAdminPreview'].forEach(k => localStorage.removeItem(k));
        window.location.href = '/portal';
      } else {
        localStorage.clear();
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auto-logout at Israel midnight — admin/worker sessions only ('expiresAt' is
// set exclusively by the worker Login.jsx). But this module (and its
// setTimeout) loads on every page, including the tenant portal on a browser
// that was previously also used to log into the admin panel on the same
// device — an `expiresAt` left over from that earlier admin session would
// otherwise fire mid-tenant-session, wipe the tenant's localStorage with the
// admin's, and send the tenant to /admin instead of /portal. Route based on
// where the browser actually is when the timer fires, not on which session
// set the timer.
function setupMidnightLogout() {
  const expiresAt = localStorage.getItem('expiresAt');
  if (!expiresAt) return;
  const isTenantPage = () => window.location.pathname.startsWith('/portal') || window.location.pathname.startsWith('/tenant-projects');
  const fire = () => {
    if (isTenantPage()) {
      ['tenantPhone','tenantToken','tenantName','tenantProject','tenantProjects','tenantBuildingAddress','tenantBuildingFull','tenantApartmentNumber','tenantBAddr','tenantBNum','tenantAptNum','tenantTab','tenantMultiApt'].forEach(k => localStorage.removeItem(k));
      window.location.href = '/portal';
    } else {
      localStorage.clear();
      window.location.href = '/login';
    }
  };
  const ms = parseInt(expiresAt) - Date.now();
  if (ms <= 0) { fire(); return; }
  setTimeout(fire, ms);
}
setupMidnightLogout();

export default api;
